// Integration related types & enums (mirrors backend enums / DTOs)

export enum IntegrationProvider {
    Salesforce = 'Salesforce',
    HubSpot = 'HubSpot',
    BigMarker = 'BigMarker',
    Webhook = 'Webhook',
    Dynamics = 'Dynamics',
}

export enum IntegrationConnectionStatus {
    PendingAuthorization = 'PendingAuthorization',
    Active = 'Active',
    Disabled = 'Disabled',
    Error = 'Error',
    AuthExpired = 'AuthExpired',
}

export enum IntegrationAuthType {
    ApiKey = 'ApiKey',
    OAuthAuthorizationCode = 'OAuthAuthorizationCode',
}

// Error entry from the bounded RecentErrors list on the integration
export interface IntegrationErrorEntryDto {
    Message: string
    Count: number
    FirstSeenUtc: string
    LastSeenUtc: string
}

// Response from the GET .../ErrorSummary endpoint
export interface IntegrationErrorSummaryDto {
    aiSummary: string | null
}

// Create DTO (subset of server-side model; keep all optional for now to align with backend)
export interface IntegrationConnectionCreateDto {
    ClientCompanyId?: string
    Provider?: IntegrationProvider | string
    Name?: string
    Description?: string
    AuthType?: IntegrationAuthType | string
    ApiKey?: string
    Channel?: string | null
    SecondarySecret?: string
    AccessToken?: string
    RefreshToken?: string
    AccessTokenExpiresUtc?: string | null
    ScopesCsv?: string
    ProviderSettingsJson?: string
    MappingConfigJson?: string
}

// View DTO (superset; may contain server-managed fields later)
export interface IntegrationConnectionViewDto extends IntegrationConnectionCreateDto {
    Id?: string
    Status?: IntegrationConnectionStatus | string
    StatusMessage?: string | null
    ConsecutiveFailureCount?: number
    IsSoftFailure?: boolean
    RecentErrors?: IntegrationErrorEntryDto[]
    // AI-generated plain-English summary of the current error state, when the server
    // has a cached value. Dashboard renders this directly to skip the ErrorSummary
    // round-trip on cache hit; null means the panel must fetch.
    AiSummary?: string | null
    StatusUpdatedUtc?: string | null
    CreatedUtc?: string
    UpdatedUtc?: string
    // Set by a disconnect, which soft-deletes: the record survives with Status=Disabled so the
    // provider can be reconnected later. Read it wherever "is this live" is the question.
    IsArchived?: boolean
    // Populated only on create responses and on Webhook-provider reset responses.
    // Plaintext — shown to the user exactly once via the one-time reveal modal and
    // never persisted in client state beyond that modal's lifetime.
    ApiKey?: string
}

// Update DTO (only provided fields are updated)
export interface IntegrationConnectionUpdateDto {
    Id: string
    ClientCompanyId: string
    Name?: string | null
    Description?: string | null
    ApiKey?: string | null
    Channel?: string | null
    IsArchived?: boolean | null
}

export type IntegrationConnectionListResponse = IntegrationConnectionViewDto[]

export const INTEGRATION_STATUS_COLORS: Record<string, string> = {
    PendingAuthorization: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    Active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    Disabled: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    AuthExpired: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
}

export const INTEGRATION_AUTH_LABELS: Record<string, string> = {
    ApiKey: 'API Key',
    OAuthAuthorizationCode: 'OAuth',
}

export const INTEGRATION_PROVIDER_LABELS: Record<string, string> = {
    Salesforce: 'Salesforce',
    HubSpot: 'HubSpot',
    BigMarker: 'BigMarker',
    Webhook: 'Webhook',
    Dynamics: 'Dynamics 365',
}
